// src/services/message.ts
import api from "../lib/api";

// ============================================
// TYPE DEFINITIONS
// ============================================

export interface Message {
  id: string;
  senderId: string;
  receiverId: string;
  content: string;
  isRead: boolean;
  readAt?: string;
  createdAt: string;
  isMessageRequest?: boolean;
  requestAccepted?: boolean | null;
  requestAcceptedAt?: string;
  sender?: {
    id: string;
    firstName: string;
    lastName: string;
    photoUrl?: string;
  };
  receiver?: {
    id: string;
    firstName: string;
    lastName: string;
    photoUrl?: string;
  };
}

export interface Conversation {
  user: {
    id: string;
    firstName: string;
    lastName: string;
    title?: string;
    organizationName?: string;
    photoUrl?: string;
  };
  lastMessage?: {
    content: string;
    createdAt: string;
    isSentByMe: boolean;
    isMessageRequest?: boolean;
    requestAccepted?: boolean | null;
  };
  unreadCount: number;
}

export interface MessageRequest {
  user: {
    id: string;
    firstName: string;
    lastName: string;
    title?: string;
    organizationName?: string;
    photoUrl?: string;
  };
  message: {
    content: string;
    createdAt: string;
    id: string;
  };
}

// ============================================
// MESSAGE ENDPOINTS
// ============================================

/**
 * Send a message to a connected user
 */
export async function sendMessage(receiverId: string, content: string): Promise<Message> {
  const { data } = await api.post('/api/messages', { receiverId, content });
  return data.data.message;
}

export interface MessageLimits {
  canSend: boolean;
  currentCount: number;
  maxCount: number;
  remaining: number;
}

export interface ConversationResponse {
  messages: Message[];
  messageRequestStatus: {
    isMessageRequest: boolean;
    requestAccepted: boolean | null;
    isSender: boolean;
  } | null;
  limitInfo?: MessageLimits | null;
}

/**
 * Get conversation with a specific user
 */
export async function getConversation(userId: string, limit = 50, offset = 0): Promise<ConversationResponse> {
  const { data } = await api.get(`/api/messages/conversation/${userId}`, {
    params: { limit, offset }
  });
  return {
    messages: data.data.messages,
    messageRequestStatus: data.data.messageRequestStatus || null,
    limitInfo: data.data.limitInfo || null
  };
}

/**
 * Get all conversations (list of users with last message)
 */
export async function getConversations(): Promise<Conversation[]> {
  const { data } = await api.get('/api/messages/conversations');
  return data.data.conversations;
}

/**
 * Mark a message as read
 */
export async function markAsRead(messageId: string): Promise<Message> {
  const { data } = await api.patch(`/api/messages/${messageId}/read`);
  return data.data.message;
}

/**
 * Get unread message count
 */
export async function getUnreadCount(): Promise<number> {
  const { data } = await api.get('/api/messages/unread-count');
  return data.data.unreadCount;
}

/**
 * Get pending message requests
 */
export async function getMessageRequests(): Promise<MessageRequest[]> {
  const { data } = await api.get('/api/messages/requests');
  return data.data.requests;
}

/**
 * Accept a message request
 */
export async function acceptMessageRequest(userId: string): Promise<Message> {
  const { data } = await api.post(`/api/messages/${userId}/accept`);
  return data.data.message;
}

/**
 * Reject a message request
 */
export async function rejectMessageRequest(userId: string): Promise<Message> {
  const { data } = await api.post(`/api/messages/${userId}/reject`);
  return data.data.message;
}
